import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuCircleCheck, LuCircleX, LuLoaderCircle, LuPackagePlus, LuPlus } from 'react-icons/lu';
import Button from '../../../../components/ui/Button';
import GlassIcon from '../../../../components/ui/GlassIcon';
import Modal from '../../../../components/ui/Modal';
import { Field, TextArea, TextInput } from '../../../../components/form/FormControls';
import { useTranslation } from '../../../../i18n/LanguageContext';
import { NativeSelect, Notice } from '../VisitUi';

const FORM_ID = 'vm-stock-add-form';

/**
 * "Malzeme Ekle" (formerly StockAddModal inside VisitModal.js). The submit,
 * validation and reset logic is unchanged; onStockAdded (handleStockAdded)
 * writes the line, updates the stock and closes this dialog.
 */
const StockAddDialog = ({ show, onHide, availableStocks, onStockAdded }) => {
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`visitModal.stock.dialog.${key}`, vars);
  const [selectedStock, setSelectedStock] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const newErrors = {};
    if (!selectedStock) newErrors.selectedStock = 'Lütfen bir stok seçin';
    if (!quantity || parseFloat(quantity) <= 0) newErrors.quantity = 'Geçerli bir miktar girin';

    // Check if quantity exceeds available stock
    const stock = availableStocks.find(s => s.id === selectedStock);
    if (stock && parseFloat(quantity) > parseFloat(stock['Kalan'])) {
      newErrors.quantity = `Maksimum ${stock['Kalan']} adet kullanılabilir`;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      // Get stock details
      const stockData = availableStocks.find(s => s.id === selectedStock);

      if (!stockData) {
        throw new Error('Seçilen stok bulunamadı');
      }

      console.log('Selected stock data:', stockData);

      // Prepare data - ensure all fields are defined
      const data = {
        stockId: selectedStock || '',
        stockName: stockData['Adı'] || stockData.Adı || '',
        stockType: stockData['Stok Tipi'] || stockData.StokTipi || '',
        quantity: parseFloat(quantity) || 0,
        reason: (reason || '').trim()
      };

      console.log('Prepared data to send:', data);

      await onStockAdded(data);

      // Reset form only after successful addition
      setSelectedStock('');
      setQuantity('');
      setReason('');
      setErrors({});
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setErrors({ submit: 'Malzeme eklenirken bir hata oluştu' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStockChange = (stockId) => {
    setSelectedStock(stockId);
    setErrors(prev => ({ ...prev, selectedStock: null }));
  };

  const handleQuantityChange = (value) => {
    setQuantity(value);
    setErrors(prev => ({ ...prev, quantity: null }));
  };

  const selectedStockData = availableStocks.find(s => s.id === selectedStock);

  return (
    <Modal
      isOpen={show}
      onClose={() => !isSubmitting && onHide()}
      size="md"
      title={label('title')}
      subtitle={label('subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30" size="xs">
          <LuPackagePlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onHide} disabled={isSubmitting}>{label('cancel')}</Button>
          <Button variant="primary" type="submit" form={FORM_ID} disabled={isSubmitting} className="max-sm:tw-flex-1">
            {isSubmitting ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {isSubmitting ? label('adding') : label('add')}
          </Button>
        </>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} className="tw-space-y-5">
        <Field label={label('stock')} htmlFor="vm-stock-select" required error={errors.selectedStock || undefined} hint={label('assignedOnly')}>
          <NativeSelect
            id="vm-stock-select"
            value={selectedStock}
            onChange={(e) => handleStockChange(e.target.value)}
            aria-invalid={errors.selectedStock ? true : undefined}
            className={errors.selectedStock ? 'tw-ring-2 tw-ring-rose-500/50' : ''}
          >
            <option value="">{label('placeholder')}</option>
            {availableStocks.length === 0 ? (
              <option disabled>{label('none')}</option>
            ) : (
              availableStocks.map(stock => {
                const remaining = parseFloat(stock['Kalan']) || 0;
                return (
                  <option key={stock.id} value={stock.id}>
                    {label('option', { name: stock['Adı'], type: stock['Stok Tipi'], remaining })}
                  </option>
                );
              })
            )}
          </NativeSelect>
        </Field>

        {selectedStockData && (
          <div className="tw-rounded-2xl tw-border tw-border-violet-500/20 tw-bg-violet-500/[0.05] tw-p-4">
            <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.1em] tw-text-violet-700 dark:tw-text-violet-300">{label('selectedInfo')}</p>
            <dl className="tw-mt-3 tw-grid tw-grid-cols-2 tw-gap-3">
              <div className="tw-min-w-0">
                <dt className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('stockName')}</dt>
                <dd className="tw-mt-0.5 tw-break-words tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{selectedStockData['Adı']}</dd>
              </div>
              <div className="tw-min-w-0">
                <dt className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('stockType')}</dt>
                <dd className="tw-mt-0.5 tw-break-words tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{selectedStockData['Stok Tipi']}</dd>
              </div>
              <div className="tw-min-w-0">
                <dt className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('available')}</dt>
                <dd className={`tw-mt-0.5 tw-text-sm tw-font-bold ${parseFloat(selectedStockData['Kalan']) < 10 ? 'tw-text-rose-600 dark:tw-text-rose-300' : 'tw-text-emerald-600 dark:tw-text-emerald-300'}`}>
                  {label('availableValue', { count: selectedStockData['Kalan'] })}
                </dd>
              </div>
              <div className="tw-min-w-0">
                <dt className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('status')}</dt>
                <dd className="tw-mt-0.5 tw-flex tw-items-center tw-gap-1 tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">
                  {selectedStockData['Durum'] === 'aktif'
                    ? <><LuCircleCheck aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-500" />{label('active')}</>
                    : <><LuCircleX aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-rose-500" />{label('inactive')}</>}
                </dd>
              </div>
            </dl>
          </div>
        )}

        <Field label={label('quantity')} htmlFor="vm-stock-quantity" required error={errors.quantity || undefined}>
          <TextInput
            id="vm-stock-quantity"
            type="number"
            step="0.01"
            min="0"
            value={quantity}
            onChange={(e) => handleQuantityChange(e.target.value)}
            placeholder={label('quantityPlaceholder')}
            error={errors.quantity || undefined}
          />
        </Field>

        <Field label={label('reason')} htmlFor="vm-stock-reason">
          <TextArea
            id="vm-stock-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={label('reasonPlaceholder')}
          />
        </Field>

        {errors.submit && <Notice tone="danger">{errors.submit}</Notice>}
      </form>
    </Modal>
  );
};

StockAddDialog.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  availableStocks: PropTypes.array.isRequired,
  onStockAdded: PropTypes.func.isRequired,
};

export default StockAddDialog;
